// The solar estimate's data for the console previews (Branding tab, Hosted page
// preview). The solar engine (roof geometry, generation, savings) runs
// server-side only, so the preview's mock API gets its solar answers from the
// signed-in console route POST /v1/widgets/{widgetId}/solar-preview
// (api.solarPreview) instead of computing anything in the browser.
//
// Pass the result as createMockApi's `solar` backend:
//   createMockApi(getPricing, { solar: solarPreviewBackend(api, widgetId) })
// When a preview shows DRAFT prices, pass getPricing (read through a ref) and call
// handle.refresh?.() after each edit so the widget re-requests the estimate.
import type { LL, SolarBackend, SolarEstimate, SolarLocateRequest, SolarLocation, SolarPricing, SolarRoof } from "@widget";
import type { SolarPreviewRequest } from "./api";

/** The one api call this needs (useApi().solarPreview). */
interface PreviewApi {
  solarPreview: <T = unknown>(widgetId: string, body: SolarPreviewRequest) => Promise<T>;
}

/**
 * The mock API's solar backend for one widget. getPricing (optional) returns the
 * draft price list to preview with; omitted (or null), the API prices with the
 * widget's saved prices.
 */
export function solarPreviewBackend(api: PreviewApi, widgetId: string, getPricing?: () => SolarPricing | null | undefined): SolarBackend {
  return {
    // Address → point on the map (approx when only the postcode matched)
    locate: (req: SolarLocateRequest) => api.solarPreview<SolarLocation>(widgetId, { action: "locate", ...req }),
    // Point → the ready-to-draw roof
    building: (at: LL) => api.solarPreview<SolarRoof>(widgetId, { action: "building", lat: at.lat, lng: at.lng }),
    // Kept panels' yields and usage → the result table, priced with the draft prices when given
    estimate: (req) => {
      const pricing = getPricing?.() ?? undefined;
      return api.solarPreview<SolarEstimate>(widgetId, { action: "estimate", ...req, ...(pricing ? { pricing } : {}) });
    },
  };
}
